import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  useTheme,
  Divider,
} from "@mui/material";

// Define item props
interface StatItem {
  title: string;
  icon: React.ReactNode;
  detailName?: string;
  value: number;
  type?: string
}

// Props for reusable card component
interface StatCardListProps {
  type: "like" | "share" | "view";
  cardTitle: string;
  data: StatItem[];
}

// StatRow sub-component
const StatRow: React.FC<StatItem & { index: number }> = ({
  title,
  icon,
  detailName,
  value,
  index,
  type
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", py: 1.5, gap: 2 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: "bold",
          fontSize: 24,
          minWidth: 24,
        }}
      >
        {index}
      </Typography>

      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Avatar
            sx={{
              bgcolor: type === "like" ? "red" :
                type === "share" ? "green":
                type === "view" ? "blue"
                : "grey",
              width: 20,
              height: 20,
              fontSize: 12,
            }}
          >
            {icon}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        </Box>

        {detailName && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
          >
            {detailName}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const StatCardList: React.FC<StatCardListProps> = ({ cardTitle, data, type }) => {
  return (
    <Box maxWidth={800} mx="auto">
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          padding: 2,
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
            transition: "all 0.3s ease-in-out",
          },
          minHeight: "600px",
          maxHeight: "600px",
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            marginBottom: 2,
            textAlign: "center",
            fontWeight: 600,
            color: "#333",
          }}
        >
          {cardTitle}
        </Typography>
        <CardContent>
          {data && data.map((item, index) => (
            <React.Fragment key={index}>
              <StatRow {...item} index={index + 1} type={type} />
              {index < data.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StatCardList;
